import { useState, useRef, useEffect, useMemo } from 'react'
import AutoResizeTextarea from './AutoResizeTextarea'
import {
  ImageIcon, Download, RefreshCw, Sparkles,
  Loader2, X, Trash2, Edit, ArrowUpDown, SortAsc, SortDesc,
  Camera, Upload, Layers
} from 'lucide-react'
import toast from 'react-hot-toast'
import client from '../api/client'
import { getCached, setCache } from '../utils/cache'

interface GeneratedImage {
  id: string
  url: string
  prompt: string
  model?: string
  created_at?: string
}

interface AiModelItem {
  id: number
  model_name: string
  display_name: string | null
  provider: string | null
  category: string
  is_paid: boolean
  icon_url: string | null
  is_active: boolean
  category_sub?: string
}

// Цены загружаются с сервера через GET /admin/ai-models/calculated-prices

const toImageItem = (m: AiModelItem, priceMap: Record<string, number>) => ({
  id: m.model_name,
  name: m.display_name || m.model_name,
  provider: m.provider || m.category || 'AI',
  tokens: priceMap[m.model_name] || 1,
})

const aspectRatios = [
  { value: '1:1', label: 'Квадрат 1:1', width: 1024, height: 1024 },
  { value: '16:9', label: 'Широкий 16:9', width: 1216, height: 832 },
  { value: '9:16', label: 'Вертикальный 9:16', width: 832, height: 1216 },
  { value: '4:3', label: 'Стандарт 4:3', width: 1152, height: 896 },
  { value: '3:4', label: 'Портрет 3:4', width: 896, height: 1152 },
  { value: '21:9', label: 'Ультраширокий 21:9', width: 1408, height: 704 },
]

type SortMode = 'newest' | 'oldest' | 'az' | 'za'
type TabMode = 'generate' | 'edit'

export default function ImageGenerator() {
  // Tabs
  const [activeTab, setActiveTab] = useState<TabMode>('generate')

  // Generate
  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = useState('')

  // Динамические модели с бэка
  const [serverModels, setServerModels] = useState<AiModelItem[]>(() => getCached<AiModelItem[]>('image_models') || [])
  const [userModels, setUserModels] = useState<string[] | null>(null)
  const [imagePrices, setImagePrices] = useState<Record<string, number>>(() => getCached<Record<string, number>>('image_prices') || {})

  const allImageModels = useMemo(() =>
    serverModels.filter(m => m.is_active && m.category === 'image' && (!m.category_sub || m.category_sub === 'generate')).map(m => toImageItem(m, imagePrices)),
  [serverModels, imagePrices])

  // Для редактора: модели с category_sub='edit' (без дублирования)
  const editModels = useMemo(() =>
    serverModels.filter(m => m.is_active && m.category === 'image' && m.category_sub === 'edit').map(m => toImageItem(m, imagePrices)),
  [serverModels, imagePrices])

  const visibleModels = userModels
    ? allImageModels.filter(m => userModels.includes(m.id))
    : allImageModels

  const visibleEditModels = userModels
    ? editModels.filter(m => userModels.includes(m.id))
    : editModels

  const defaultModel = allImageModels[0]?.id || ''
  const [selectedModel, setSelectedModel] = useState(defaultModel)
  const [selectedRatio, setSelectedRatio] = useState(aspectRatios[0].value)
  const [supportedRatios, setSupportedRatios] = useState<Record<string, string[]>>({})
  const [imageCount, setImageCount] = useState(1)
  const [isGenerating, setIsGenerating] = useState(false)

  // Editor
  const [editPrompt, setEditPrompt] = useState('')
  const [editNegativePrompt, setEditNegativePrompt] = useState('')
  const [editImages, setEditImages] = useState<string[]>([]) // data URLs
  const editFileRef = useRef<HTMLInputElement>(null)
  const [editUploadIdx, setEditUploadIdx] = useState(0) // which slot to fill next

  // Gallery
  const [images, setImages] = useState<GeneratedImage[]>([])
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null)
  const [showModelSelector, setShowModelSelector] = useState(false)
  const [sortMode, setSortMode] = useState<SortMode>('newest')
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [paginatedTotal, setPaginatedTotal] = useState(0)
  const [paginatedOffset, setPaginatedOffset] = useState(0)
  const ITEMS_PER_PAGE = 20
  const [loadingModels, setLoadingModels] = useState(true)

  useEffect(() => {
    loadModels()
  }, [])

  const loadModels = async () => {
    setLoadingModels(true)
    setLoadingHistory(true)
    try {
      const [modelsResp, pricesResp, settings, avail] = await Promise.all([
        client.get('/models/ai-models'),
        client.get('/admin/ai-models/calculated-prices').catch(() => ({ data: {} })),
        client.get('/models/my'),
        client.get('/models/available'),
      ])
      // Модели — сохраняем сразу
      const items: AiModelItem[] = modelsResp.data || []
      setServerModels(items)
      setCache('image_models', items)

      // Цены
      const rawPrices: Record<string, { input: number; output: number; unit: string; cost_usd: number }> = pricesResp.data || {}
      const starsPrices: Record<string, number> = {}
      for (const [modelName, info] of Object.entries(rawPrices)) {
        if (info.unit === 'stars' && typeof info.input === 'number') {
          starsPrices[modelName] = info.input
        }
      }
      setImagePrices(starsPrices)
      setCache('image_prices', starsPrices)

      // Поддерживаемые соотношения
      if (avail.data?.model_supported_ratios) {
        setSupportedRatios(avail.data.model_supported_ratios)
      }

      // Настройки пользователя
      const enabled: string[] = settings.data?.image_models
      if (enabled && enabled.length > 0) {
        setUserModels(enabled)
        const active = items.filter(m => m.is_active && m.category === 'image').map(m => toImageItem(m, starsPrices))
        const first = active.find((m) => enabled.includes(m.id))
        if (first) setSelectedModel(first.id)
      }

      setLoadingModels(false)

      // После того как модели загружены — грузим галерею
      loadGallery()
    } catch {
      setLoadingModels(false)
      setLoadingHistory(false)
    }
  }

  const loadGallery = async () => {
    try {
      const { data } = await client.get('/image/history/paginated', {
        params: { limit: ITEMS_PER_PAGE, offset: 0 }
      })
      setImages(data.items || [])
      setPaginatedTotal(data.total || 0)
      setPaginatedOffset(ITEMS_PER_PAGE)
    } catch {
      // ignore
    } finally {
      setLoadingHistory(false)
    }
  }

  // Всегда ставим первую модель при загрузке списка (если selectedModel не входит в список)
  useEffect(() => {
    if (allImageModels.length > 0) {
      const validIds = allImageModels.map(m => m.id)
      if (!selectedModel || !validIds.includes(selectedModel)) {
        setSelectedModel(allImageModels[0].id)
      }
    }
  }, [allImageModels])

  // При переключении на вкладку "Редактор фото" — если выбранная модель не поддерживает редактор,
  // переключаемся на первую подходящую
  useEffect(() => {
    if (activeTab === 'edit' && visibleEditModels.length > 0) {
      if (!visibleEditModels.find(m => m.id === selectedModel)) {
        setSelectedModel(visibleEditModels[0].id)
      }
    }
  }, [activeTab, visibleEditModels])

  const sortedImages = [...images].sort((a, b) => {
    switch (sortMode) {
      case 'oldest': return (a.created_at || '').localeCompare(b.created_at || '')
      case 'az': return a.prompt.localeCompare(b.prompt)
      case 'za': return b.prompt.localeCompare(a.prompt)
      default: return (b.created_at || '').localeCompare(a.created_at || '')
    }
  })

  // Editor file upload
  // Editor file upload — до 3 фото, по слотам 0,1,2
  const handleEditFileUpload = (slotIdx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const file = files[0]
    const reader = new FileReader()
    reader.onload = (ev) => {
      if (ev.target?.result) {
        setEditImages((prev) => {
          const next = [...prev]
          next[slotIdx] = ev.target!.result as string
          return next
        })
      }
    }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

  const removeEditImage = (idx: number) => {
    setEditImages((prev) => {
      const next = [...prev]
      delete next[idx]
      return next
    })
  }

  // GPT-5.4-image-2 — очень долгая модель (5-7 минут)
  const SLOW_MODELS = ['openai/gpt-5.4-image-2']
  const isSlowModel = SLOW_MODELS.includes(selectedModel)
  const [pendingImages, setPendingImages] = useState<string[]>([])

  // Generate
  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return
    setIsGenerating(true)

    // Добавляем плейсхолдер для долгих моделей
    const pendingIds: string[] = []
    if (isSlowModel) {
      for (let i = 0; i < imageCount; i++) {
        const pid = `pending-${Date.now()}-${i}`
        pendingIds.push(pid)
      }
      setPendingImages(pendingIds)
    }

    try {
      const ratio = aspectRatios.find((r) => r.value === selectedRatio) || aspectRatios[0]
      const timeout = SLOW_MODELS.includes(selectedModel) ? 420000 : 120000
      const { data } = await client.post('/image/generate', {
        prompt: prompt.trim(),
        negativePrompt: negativePrompt.trim(),
        model: selectedModel,
        aspectRatio: selectedRatio,
        width: ratio.width,
        height: ratio.height,
        n: imageCount,
      }, { timeout })
      if (Array.isArray(data)) {
        setImages((prev) => [...data, ...prev])
      } else {
        setImages((prev) => [data, ...prev])
      }
      toast.success(`Создано изображений: ${Array.isArray(data) ? data.length : 1}`)
    } catch (err: any) {
      const detail = err?.response?.data?.detail || ''
      toast.error('Ошибка при генерации' + (detail ? ': ' + detail : '') + '. ⭐ Звёзды возвращены')
    } finally {
      setIsGenerating(false)
      setPendingImages([])
    }
  }

  // Edit / Remix
  const handleEditSubmit = async () => {
    if (!editPrompt.trim() || isGenerating) return
    setIsGenerating(true)

    // Добавляем плейсхолдер для долгих моделей
    const pendingIds: string[] = []
    if (isSlowModel) {
      for (let i = 0; i < imageCount; i++) {
        const pid = `pending-${Date.now()}-edit-${i}`
        pendingIds.push(pid)
      }
      setPendingImages(pendingIds)
    }

    try {
      const ratio = aspectRatios.find((r) => r.value === selectedRatio) || aspectRatios[0]
      const timeout = SLOW_MODELS.includes(selectedModel) ? 420000 : 120000
      const { data } = await client.post('/image/generate', {
        prompt: editPrompt.trim(),
        model: selectedModel,
        negativePrompt: editNegativePrompt.trim(),
        aspectRatio: selectedRatio,
        width: ratio.width,
        height: ratio.height,
        n: imageCount,
        image_urls: editImages,
      }, { timeout })
      if (Array.isArray(data)) {
        setImages((prev) => [...data, ...prev])
      } else {
        setImages((prev) => [data, ...prev])
      }
      toast.success(`Создано изображений: ${Array.isArray(data) ? data.length : 1}`)
    } catch (err: any) {
      const detail = err?.response?.data?.detail || ''
      toast.error('Ошибка при обработке' + (detail ? ': ' + detail : '') + '. ⭐ Звёзды возвращены')
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDeleteImage = async (id: string) => {
    try {
      await client.delete(`/image/${id}`)
      setImages((prev) => prev.filter((img) => img.id !== id))
      toast.success('Изображение удалено')
    } catch {
      toast.error('Ошибка при удалении')
    }
  }

  const handleDeleteAll = async () => {
    if (images.length === 0) return
    try {
      await client.delete('/image')
      setImages([])
      toast.success('Галерея очищена')
    } catch {
      toast.error('Ошибка при очистке')
    }
  }

  const handleDownload = async (url: string, prompt: string) => {
    const filename = `all-neurals-${prompt.slice(0, 30).replace(/\s+/g, '-')}.png`

    if (url.startsWith('data:')) {
      const byteStr = atob(url.split(',')[1])
      const mime = url.split(';')[0].split(':')[1]
      const ab = new ArrayBuffer(byteStr.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i)
      const blob = new Blob([ab], { type: mime })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
      toast.success('Скачивание начато')
      return
    }

    // Внешний URL — скачиваем через fetch, чтобы не открывать вкладку
    try {
      const resp = await fetch(url, { mode: 'cors' })
      const blob = await resp.blob()
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
      toast.success('Скачивание начато')
    } catch {
      window.open(url, '_blank')
      toast.success('Изображение открыто в новой вкладке')
    }
  }

  const selectedModelData = (visibleModels.length > 0 ? visibleModels : allImageModels).find((m) => m.id === selectedModel)
  const editModelData = visibleEditModels.find((m) => m.id === selectedModel)
  // Модель для показа во вкладке редактора: если выбранная модель не входит в editModels,
  // показываем первую доступную редактор-модель
  const currentEditModel = editModelData || visibleEditModels[0]

  // Модели, которые не соблюдают размерности — только 1:1
  const SQUARE_ONLY_MODELS = [
    'google/gemini-2.5-flash-image',
    'openai/gpt-5-image',
    'openai/gpt-5-image-mini',
    'openai/gpt-5.4-image-2',
    'fal-ai/gpt-image-1.5/edit',
    'fal-ai/gpt-image-1-mini/edit',
  ]

  // Фильтруем доступные соотношения сторон под выбранную модель
  const availableRatios = aspectRatios.filter((r) => {
    const allowed = supportedRatios[selectedModel]
    const baseCheck = !allowed || allowed.includes(r.value)
    if (!baseCheck) return false
    // Для указанных моделей — только квадрат
    if (SQUARE_ONLY_MODELS.includes(selectedModel)) {
      return r.value === '1:1'
    }
    return true
  })

  // Если текущий ratio не поддерживается моделью — переключаем на первый доступный
  useEffect(() => {
    if (availableRatios.length > 0 && !availableRatios.find((r) => r.value === selectedRatio)) {
      setSelectedRatio(availableRatios[0].value)
    }
  }, [selectedModel, selectedRatio])

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
          <ImageIcon className="w-6 h-6" />
          Генерация изображений
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Создавайте и редактируйте изображения с помощью AI-моделей
        </p>
      </div>

      {/* Tabs: Generate / Edit */}
      <div className="flex gap-2 border-b border-white/10 pb-3">
        <button
          onClick={() => setActiveTab('generate')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
            activeTab === 'generate'
              ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Генерация
        </button>
        <button
          onClick={() => { setActiveTab('edit') }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
            activeTab === 'edit'
              ? 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/20'
              : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
          }`}
        >
          <Edit className="w-4 h-4" />
          Редактор фото
        </button>
      </div>

      {/* Input area - Generate */}
      {activeTab === 'generate' && (
        <div className="glass-card p-6 space-y-4">
          {/* Model selector */}
          <div className="relative">
            <div className="text-xs text-gray-500 mb-1.5">Модель AI</div>
            <button
              onClick={() => setShowModelSelector(!showModelSelector)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl glass text-sm text-gray-300 hover:text-white transition-all w-full"
            >
              <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
              <span className="truncate">{selectedModelData?.name}</span>
              <span className="text-gray-600">·</span>
              <span className="text-gray-500 truncate">{selectedModelData?.provider}</span>
              <span className="ml-auto text-indigo-400/70 text-xs font-mono"> ✦{selectedModelData?.tokens || 0}</span>
            </button>

            {showModelSelector && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowModelSelector(false)} />
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl z-50 p-2 space-y-0.5 max-h-80 overflow-y-auto whitespace-normal">
                  {(visibleModels.length > 0 ? visibleModels : allImageModels).map((model) => (
                    <button
                      key={model.id}
                      onClick={() => { setSelectedModel(model.id); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        selectedModel === model.id
                          ? 'bg-indigo-500/20 text-indigo-300'
                          : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                      }`}
                    >
                      <span className="font-medium">{model.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{model.provider}</span>
                      {model.name === 'openai/gpt-5.4-image-2' && (
                        <span className="ml-2 text-[10px] text-amber-400/70">~5-7 мин</span>
                      )}
                      <span className="ml-auto text-indigo-400/60 text-[10px] font-mono"> ✦{model.tokens}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="text-[10px] text-gray-600 mt-1.5">✦ — стоимость в звёздах</p>
          </div>

          {/* Prompt */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">
              Prompt (описание изображения)
            </label>
            <AutoResizeTextarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Опишите изображение, которое хотите создать..."
            />
          </div>

          {/* Negative prompt */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">
              Negative prompt (чего избегать)
            </label>
            <AutoResizeTextarea
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder="Что НЕ должно быть на изображении..."
            />
          </div>

          {/* Aspect ratio + Count row */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-2">
                Соотношение сторон
              </label>
              <div className="flex flex-wrap gap-2">
                {availableRatios.map((ratio) => (
                  <button
                    key={ratio.value}
                    onClick={() => setSelectedRatio(ratio.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                      selectedRatio === ratio.value
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'glass text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="shrink-0">
              <label className="block text-xs text-gray-500 mb-2">
                Количество
              </label>
              <div className="flex gap-1.5 items-center">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => setImageCount(n)}
                    className={`w-9 h-9 rounded-lg text-sm transition-all ${
                      imageCount === n
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'glass text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || isGenerating}
            className="gradient-btn w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40 flex-col"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Генерация...{isSlowModel ? ' (это может занять 5-7 минут)' : ''}</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Сгенерировать x{imageCount} — ✦{selectedModelData?.tokens || 0}
                </span>
                {isSlowModel && (
                  <span className="text-[10px] text-white/80 font-normal">
                    ⚠ Время генерации ~5-7 минут. Не закрывайте страницу.
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      )}

      {/* Input area - Edit */}
      {activeTab === 'edit' && (
        <div className="glass-card p-6 space-y-4">
          {/* Model selector — для редактора только Fal.ai модели */}
          <div className="relative">
            <div className="text-xs text-gray-500 mb-1.5">Модель AI (только модели с поддержкой img2img)</div>
            <button
              onClick={() => setShowModelSelector(!showModelSelector)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl glass text-sm text-gray-300 hover:text-white transition-all w-full"
            >
              <Sparkles className="w-4 h-4 text-indigo-400 shrink-0" />
              <span className="truncate">{currentEditModel?.name}</span>
              <span className="text-gray-600">·</span>
              <span className="text-gray-500 truncate">{currentEditModel?.provider}</span>
              <span className="ml-auto text-indigo-400/70 text-xs font-mono"> ✦{Math.round((currentEditModel?.tokens || 0) * 1.3)}</span>
            </button>

            {showModelSelector && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowModelSelector(false)} />
                <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a2e] border border-white/10 rounded-xl shadow-2xl z-50 p-2 space-y-0.5 max-h-80 overflow-y-auto whitespace-normal">
                  {visibleEditModels.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => { setSelectedModel(model.id); setShowModelSelector(false) }}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        selectedModel === model.id
                          ? 'bg-indigo-500/20 text-indigo-300'
                          : 'text-gray-400 hover:bg-white/[0.06] hover:text-gray-200'
                      }`}
                    >
                      <span className="font-medium">{model.name}</span>
                      <span className="ml-2 text-xs text-gray-500">{model.provider}</span>
                      {model.name === 'openai/gpt-5.4-image-2' && (
                        <span className="ml-2 text-[10px] text-amber-400/70">~5-7 мин</span>
                      )}
                      <span className="ml-auto text-indigo-400/60 text-[10px] font-mono"> ✦{Math.round(model.tokens * 1.3)}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
            <p className="text-[10px] text-gray-600 mt-1.5">✦ — стоимость в звёздах (редактор ×1.3)</p>
          </div>

          {/* Upload images — 3 фиксированных слота */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">
              Загрузите до 3 фото для редактирования
            </label>
            <div className="flex flex-wrap gap-3">
              {[0, 1, 2].map((slotIdx) => (
                <div key={slotIdx} className="relative w-28 h-28 rounded-xl overflow-hidden glass border border-white/10">
                  {editImages[slotIdx] ? (
                    <>
                      <img src={editImages[slotIdx]} alt={`Upload ${slotIdx + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeEditImage(slotIdx)}
                        className="absolute top-1 right-1 p-1 rounded-lg bg-black/60 backdrop-blur-sm text-red-400 hover:text-red-300"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        const input = document.createElement('input')
                        input.type = 'file'
                        input.accept = 'image/*'
                        input.onchange = ((e: Event) => handleEditFileUpload(slotIdx)(e as unknown as React.ChangeEvent<HTMLInputElement>))
                        input.click()
                      }}
                      className="w-full h-full rounded-xl border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-gray-500 hover:text-gray-300 hover:border-indigo-500/30 transition-all bg-transparent"
                    >
                      <Upload className="w-5 h-5" />
                      <span className="text-[10px]">Фото {slotIdx + 1}</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Prompt for edit */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">
              Опишите, что нужно изменить на фото
            </label>
            <AutoResizeTextarea
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              placeholder="Пример: Сделай фон светлее, добавь небо с облаками..."
            />
          </div>

          {/* Negative prompt for edit */}
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">
              Negative prompt (чего избегать)
            </label>
            <AutoResizeTextarea
              value={editNegativePrompt}
              onChange={(e) => setEditNegativePrompt(e.target.value)}
              placeholder="Что НЕ должно быть на изображении..."
            />
          </div>

          {/* Aspect ratio + Count */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-xs text-gray-500 mb-2">Соотношение сторон</label>
              <div className="flex flex-wrap gap-2">
                {availableRatios.map((ratio) => (
                  <button
                    key={ratio.value}
                    onClick={() => setSelectedRatio(ratio.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs transition-all ${
                      selectedRatio === ratio.value
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'glass text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {ratio.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="shrink-0">
              <label className="block text-xs text-gray-500 mb-2">Количество</label>
              <div className="flex gap-1.5 items-center">
                {[1, 2, 3, 4].map((n) => (
                  <button
                    key={n}
                    onClick={() => setImageCount(n)}
                    className={`w-9 h-9 rounded-lg text-sm transition-all ${
                      imageCount === n
                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                        : 'glass text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Edit button */}
          <button
            onClick={handleEditSubmit}
            disabled={editImages.length === 0 || !editPrompt.trim() || isGenerating}
            className="gradient-btn w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-40 flex-col"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Обработка...{isSlowModel ? ' (это может занять 5-7 минут)' : ''}</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-2">
                  <Edit className="w-4 h-4" />
                  Редактировать x{imageCount} — ✦{Math.round((currentEditModel?.tokens || 0) * 1.3 * imageCount)}
                </span>
                {isSlowModel && (
                  <span className="text-[10px] text-white/80 font-normal">
                    ⚠ Время генерации ~5-7 минут. Не закрывайте страницу.
                  </span>
                )}
              </>
            )}
          </button>
        </div>
      )}

      {/* Gallery */}
      {images.length > 0 && (
        <div className="flex items-center justify-between gap-4">
          <h2 className="text-sm font-semibold text-gray-300">
            Результаты
            <span className="ml-2 text-xs font-normal text-gray-500">({images.length})</span>
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const modes: SortMode[] = ['newest', 'oldest', 'az', 'za']
                const idx = modes.indexOf(sortMode)
                setSortMode(modes[(idx + 1) % modes.length])
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass text-xs text-gray-400 hover:text-gray-200 transition-all"
              title={`Sort: ${sortMode}`}
            >
              {sortMode === 'newest' && <SortDesc className="w-3 h-3" />}
              {sortMode === 'oldest' && <SortAsc className="w-3 h-3" />}
              {sortMode === 'az' && <ArrowUpDown className="w-3 h-3" />}
              {sortMode === 'za' && <ArrowUpDown className="w-3 h-3" />}
              {sortMode === 'newest' && 'Новые'}
              {sortMode === 'oldest' && 'Старые'}
              {sortMode === 'az' && 'А-Я'}
              {sortMode === 'za' && 'Я-А'}
            </button>
            <button
              onClick={handleDeleteAll}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
            >
              <Trash2 className="w-3 h-3" />
              Удалить всё
            </button>
          </div>
        </div>
      )}

      {loadingHistory && images.length === 0 && (
        <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
          Загрузка изображений...
        </div>
      )}

      {sortedImages.length > 0 && (
          <div className="space-y-6">
<div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Плейсхолдеры для долгой генерации */}
            {pendingImages.map((pid) => (
              <div key={pid} className="glass-card overflow-hidden">
                <div className="aspect-square bg-white/5 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3 text-gray-500">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                    <span className="text-xs text-indigo-400/60">Генерируется...</span>
                  </div>
                </div>
                <div className="p-2">
                  <p className="text-[10px] text-indigo-400/80 truncate">{selectedModelData?.name || ''}</p>
                  <p className="text-xs text-gray-500 truncate">~5-7 мин</p>
                </div>
              </div>
            ))}
            {sortedImages.map((img) => (
              <div key={img.id} className="glass-card overflow-hidden group relative">
                <div
                  className="aspect-square bg-white/5 flex items-center justify-center overflow-hidden cursor-pointer"
                  onClick={() => setSelectedImage(img)}
                >
                  <img
                    src={img.url}
                    alt={img.prompt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleDownload(img.url, img.prompt)}
                    className="p-1.5 rounded-lg bg-black/60 backdrop-blur-sm text-gray-300 hover:text-white hover:bg-black/80 transition-all"
                    title="Скачать"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeleteImage(img.id)}
                    className="p-1.5 rounded-lg bg-black/60 backdrop-blur-sm text-red-400 hover:text-red-300 hover:bg-red-900/60 transition-all"
                    title="Удалить"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-2 space-y-0.5">
                  <p className="text-[10px] text-indigo-400/80 truncate">{img.model || ''}</p>
                  <p className="text-xs text-gray-400 truncate">{img.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

            {images.length < paginatedTotal && (
              <div className="flex justify-center">
                <button
                  onClick={async () => {
                    try {
                      const { data } = await client.get('/image/history/paginated', {
                        params: { limit: ITEMS_PER_PAGE, offset: paginatedOffset }
                      })
                      setImages(prev => [...prev, ...(data.items || [])])
                      setPaginatedTotal(data.total || 0)
                      setPaginatedOffset(paginatedOffset + ITEMS_PER_PAGE)
                    } catch (e) { console.error(e) }
                  }}
                  className="px-6 py-3 rounded-xl glass text-sm text-gray-300 hover:text-white transition-all border border-white/10 hover:border-indigo-500/30"
                >
                  Показать ещё ({images.length} из {paginatedTotal})
                </button>
              </div>
            )}
          </div>
      )}

      {/* Image viewer modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-gray-300 truncate flex-1 mr-4">
                {selectedImage.prompt}
              </p>
              <div className="flex items-center gap-2">
                <button
                  className="p-2 rounded-lg glass text-gray-400 hover:text-white transition-all"
                  onClick={() => handleDownload(selectedImage.url, selectedImage.prompt)}
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  className="p-2 rounded-lg glass text-red-400 hover:text-red-300 transition-all"
                  onClick={() => { handleDeleteImage(selectedImage.id); setSelectedImage(null) }}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  className="p-2 rounded-lg glass text-gray-400 hover:text-white transition-all"
                  onClick={() => setSelectedImage(null)}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="max-h-[80vh] overflow-hidden rounded-2xl flex items-center justify-center bg-black/30">
              <img
                src={selectedImage.url}
                alt={selectedImage.prompt}
                className="w-full h-full object-contain max-h-[80vh]"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
